import { ApplicationConfig, importProvidersFrom, isDevMode, provideZoneChangeDetection, APP_INITIALIZER } from '@angular/core';
import { provideRouter, withEnabledBlockingInitialNavigation, withInMemoryScrolling } from '@angular/router';
import { HttpClient, provideHttpClient, withFetch, withInterceptors, withXsrfConfiguration } from '@angular/common/http';
import { provideServiceWorker } from '@angular/service-worker';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeuix/themes/aura';
import { definePreset } from '@primeuix/themes';
import { registerLocaleData } from '@angular/common';
import en from '@angular/common/locales/en';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { MissingTranslationHandler, MissingTranslationHandlerParams, TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { Observable, firstValueFrom, map } from 'rxjs';
import { AppUpdateService } from './core/services/app-update.service';

/**
 * Idioma del que se toman los textos cuando el activo no tiene la clave. El
 * loader de abajo lo necesita para saber a qué idioma hay que dejar intacto.
 */
const DEFAULT_LANGUAGE = 'en';

/**
 * ngx-translate solo cae al idioma por defecto cuando una clave no existe
 * (compara el valor con `undefined`), así que un valor `""` en es.json se pinta
 * tal cual y deja la etiqueta en blanco: en un navegador en español los títulos
 * de «Trip Statistics» o «Incident Details» salían vacíos aunque existieran.
 *
 * Descartar los valores vacíos al cargar el idioma los convierte en «no existe»
 * y activa el fallback a inglés. El idioma por defecto no se poda, es el
 * diccionario del que se cae; lo que sí está vacío también en inglés se sigue
 * pintando vacío en lugar de mostrar la clave cruda.
 */
function withoutEmptyValues(value: unknown): unknown {
    if (Array.isArray(value)) {
        return value.map(withoutEmptyValues);
    }

    if (typeof value === 'string') {
        return value.trim() === '' ? undefined : value;
    }

    if (value !== null && typeof value === 'object') {
        const children: Record<string, unknown> = {};
        for (const [key, child] of Object.entries(value)) {
            const parsed = withoutEmptyValues(child);
            if (parsed !== undefined) {
                children[key] = parsed;
            }
        }
        return children;
    }

    return value;
}

export class EmptyValueFallbackTranslateLoader extends TranslateHttpLoader {
    constructor(http: HttpClient) {
        super(http, 'assets/i18n/', '.json');
    }

    override getTranslation(lang: string): Observable<Object> {
        const translations = super.getTranslation(lang);

        if (lang === DEFAULT_LANGUAGE) {
            return translations;
        }

        return translations.pipe(map((value) => withoutEmptyValues(value) as Object));
    }
}

/**
 * Falls back to English when a translation key is missing. Empty-string values
 * never reach here: EmptyValueFallbackTranslateLoader removes them at load time
 * so ngx-translate treats them as the absent keys they effectively are.
 */
export class FallbackTranslationHandler implements MissingTranslationHandler {
    handle(params: MissingTranslationHandlerParams): string {
        if (params.translateService.defaultLang && params.translateService.defaultLang !== params.translateService.currentLang) {
            const defaultTranslations = params.translateService.translations[params.translateService.defaultLang];
            if (defaultTranslations) {
                const keys = params.key.split('.');
                let value: any = defaultTranslations;
                for (const k of keys) {
                    value = value?.[k];
                }
                if (typeof value === 'string' && value) return value;
            }
        }
        return params.key;
    }
}

/**
 * Idiomas que tienen diccionario en `assets/i18n`.
 */
const SUPPORTED_LANGUAGES = ['en', 'es'];

/**
 * Tiempo máximo que se espera al diccionario antes de dejar arrancar la
 * aplicación. Si `assets/i18n/<idioma>.json` no responde (red cortada, 404),
 * más vale pintar la interfaz en inglés que dejar la pantalla en blanco.
 */
const TRANSLATION_TIMEOUT_MS = 5000;

/**
 * Elige el idioma y carga su diccionario antes de que exista ningún componente.
 *
 * `ngOnInit` de los componentes solo se ejecuta una vez, y hay pantallas que
 * leen el diccionario ahí con `translateService.get(...)` y se guardan los
 * textos (la lista de Recruiting, por ejemplo). Si el `use()` del idioma corría
 * en paralelo al arranque, esas pantallas se quedaban con el inglés de
 * respaldo hasta recargar. Al resolver esta promesa como `APP_INITIALIZER` la
 * navegación inicial y el primer render esperan al diccionario, y `es.json`
 * también queda cargado del tirón para el resto de claves.
 *
 * El inglés se precarga cuando el idioma no es inglés porque el
 * `MissingTranslationHandler` de arriba es el fallback: sin él, una clave que
 * solo exista en `en.json` se pinta cruda.
 */
export function translateInitializerFactory(translate: TranslateService): () => Promise<void> {
    return (): Promise<void> => {
        // `getBrowserLang` ya devuelve el subcódigo ('es'), pero se recorta por
        // si algún día llega la etiqueta completa ('es-ES').
        const browserLang = (translate.getBrowserLang() || '').split('-')[0].toLowerCase();
        const lang = SUPPORTED_LANGUAGES.includes(browserLang) ? browserLang : DEFAULT_LANGUAGE;

        translate.setDefaultLang(DEFAULT_LANGUAGE);

        const pending: Array<Promise<unknown>> = [firstValueFrom(translate.use(lang)).catch(() => undefined)];

        if (lang !== DEFAULT_LANGUAGE) {
            pending.push(firstValueFrom(translate.getTranslation(DEFAULT_LANGUAGE)).catch(() => undefined));
        }

        const loaded = Promise.all(pending).then(() => undefined);
        const timeout = new Promise<void>((resolve) => setTimeout(resolve, TRANSLATION_TIMEOUT_MS));

        return Promise.race([loaded, timeout]);
    };
}

import { routes } from './app.routes';
import { authInterceptor } from './shared/interceptors/auth.interceptor';
import { errorInterceptor } from './shared/interceptors/error.interceptor';

registerLocaleData(en);

export function httpLoaderFactory(http: HttpClient): TranslateHttpLoader {
    return new EmptyValueFallbackTranslateLoader(http);
}

const customPreset = definePreset(Aura, {
    semantic: {
        primary: {
            50: '{blue.50}',
            100: '{blue.100}',
            200: '{blue.200}',
            300: '{blue.300}',
            400: '{blue.400}',
            500: '{blue.500}',
            600: '{blue.600}',
            700: '{blue.700}',
            800: '{blue.800}',
            900: '{blue.900}',
            950: '{blue.950}'
        },
        overlay: {
            modal: {
                borderRadius: '1.5rem'
            },
            popover: {
                borderRadius: '10px'
            }
        },
        colorScheme: {
            light: {
                surface: {
                    0: 'color-mix(in srgb, {primary.950}, white 100%)',
                    50: 'color-mix(in srgb, {primary.950}, white 95%)',
                    100: 'color-mix(in srgb, {primary.950}, white 90%)',
                    200: 'color-mix(in srgb, {primary.950}, white 80%)',
                    300: 'color-mix(in srgb, {primary.950}, white 70%)',
                    400: 'color-mix(in srgb, {primary.950}, white 60%)',
                    500: 'color-mix(in srgb, {primary.950}, white 50%)',
                    600: 'color-mix(in srgb, {primary.950}, white 40%)',
                    700: 'color-mix(in srgb, {primary.950}, white 30%)',
                    800: 'color-mix(in srgb, {primary.950}, white 20%)',
                    900: 'color-mix(in srgb, {primary.950}, white 10%)',
                    950: 'color-mix(in srgb, {primary.950}, white 5%)'
                }
            },
            dark: {
                surface: {
                    0: 'color-mix(in srgb, var(--surface-ground), white 100%)',
                    50: 'color-mix(in srgb, var(--surface-ground), white 95%)',
                    100: 'color-mix(in srgb, var(--surface-ground), white 90%)',
                    200: 'color-mix(in srgb, var(--surface-ground), white 80%)',
                    300: 'color-mix(in srgb, var(--surface-ground), white 70%)',
                    400: 'color-mix(in srgb, var(--surface-ground), white 60%)',
                    500: 'color-mix(in srgb, var(--surface-ground), white 50%)',
                    600: 'color-mix(in srgb, var(--surface-ground), white 40%)',
                    700: 'color-mix(in srgb, var(--surface-ground), white 30%)',
                    800: 'color-mix(in srgb, var(--surface-ground), white 20%)',
                    900: 'color-mix(in srgb, var(--surface-ground), white 10%)',
                    950: 'color-mix(in srgb, var(--surface-ground), white 5%)'
                }
            }
        }
    }
});

export const appConfig: ApplicationConfig = {
    providers: [
        provideZoneChangeDetection({ eventCoalescing: true }),
        provideRouter(
            routes,
            withInMemoryScrolling({
                anchorScrolling: 'enabled',
                scrollPositionRestoration: 'top'
            }),
            withEnabledBlockingInitialNavigation()
        ),
        importProvidersFrom(
            FormsModule,
            TranslateModule.forRoot({
                defaultLanguage: DEFAULT_LANGUAGE,
                loader: {
                    provide: TranslateLoader,
                    useFactory: httpLoaderFactory,
                    deps: [HttpClient]
                },
                missingTranslationHandler: {
                    provide: MissingTranslationHandler,
                    useClass: FallbackTranslationHandler
                },
                useDefaultLang: true
            })
        ),
        provideAnimationsAsync(),
        provideHttpClient(withFetch(), withInterceptors([authInterceptor, errorInterceptor]), withXsrfConfiguration({ cookieName: 'XSRF-TOKEN', headerName: 'X-XSRF-TOKEN' })),
        providePrimeNG({ theme: { preset: customPreset, options: { darkModeSelector: '.app-dark' } } }),
        MessageService,
        provideServiceWorker('ngsw-worker.js', {
            enabled: !isDevMode(),
            registrationStrategy: 'registerWhenStable:30000'
        }),
        // Dejar arrancar la aplicación con el diccionario del idioma ya cargado
        {
            provide: APP_INITIALIZER,
            useFactory: translateInitializerFactory,
            deps: [TranslateService],
            multi: true
        },
        // Inicializar servicio de actualización automática
        {
            provide: APP_INITIALIZER,
            useFactory: (appUpdateService: AppUpdateService) => () => appUpdateService,
            deps: [AppUpdateService],
            multi: true
        }
    ]
};
